// TTSSelfContainedUI.js
// Floating Stop + Volume UI (vertical slider in a floating popover above a compact pill)

export function mountTTSSelfContainedUI(tts, opts = {}) {
  if (!tts)
    throw new Error("mountTTSSelfContainedUI: tts instance is required");
  if (window.__ttsOverlayMounted) return window.__ttsOverlayMounted; // singleton

  const position = opts.position || "bottom-right";
  const fadeMs = opts.fadeMs === undefined ? 160 : Number(opts.fadeMs);

  // Theming
  const accent = opts.accentColor || "#ff4d57";
  const accentSoft = opts.accentSoft || "#ff7a82";
  const glassBg = opts.glassBg || "rgba(18, 18, 22, 0.55)";
  const glassBorder = opts.glassBorder || "rgba(255,255,255,0.16)";
  const glassShadow = opts.glassShadow || "0 10px 30px rgba(0,0,0,.28)";
  const zIndex = opts.zIndex === undefined ? 10 : Number(opts.zIndex); // ← default 10

  // --- host + shadow dom ---
  const host = document.createElement("div");
  host.id = "tts-overlay-host";
  // Full-bleed, click-through host. The Stop pill and Volume pill are positioned
  // INDEPENDENTLY inside the shadow root: Volume sits in the bottom-left corner,
  // and the Stop pill floats just to the left of the centered chat input bar. The
  // host therefore spans the whole viewport and lets each child own its placement.
  // pointer-events:none keeps the 3D scene clickable; the pills re-enable events.
  Object.assign(host.style, {
    position: "fixed",
    inset: "0",
    zIndex: String(zIndex),
    pointerEvents: "none",
  });

  const PAD = opts.padding === undefined ? 18 : Number(opts.padding);

  const shadow = host.attachShadow({ mode: "open" });

  shadow.innerHTML = `
<style>
  :host, .wrap { all: initial; font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial; }

  /* ---------- Size tokens (desktop defaults) ---------- */
  :host {
    /* Stop pill */
    --stop-h: 44px;
    --stop-pad-x: 18px;
    --stop-font: 14.5px;

    /* Volume pill & buttons */
    --vol-pad-y: 6px;
    --vol-pad-x: 6px;
    --vol-btn: 36px;         /* icon button circle — matches .chat-icon-btn */
    --icon: 18px;            /* svg icon size */

    /* Slider */
    --slider-track-h: 5px;
    --slider-knob: 18px;
    --slider-wrap-w: 42px;
    --slider-wrap-h: 150px;  /* vertical travel (height of the whole slider area) */

    /* Text */
    --pct-font: 12.5px;

    /* Gaps */
    --wrap-gap: 12px;
    --vol-gap: 10px;
    --popover-gap: 8px;
  }

  /* Full-bleed click-through layer; each pill is positioned independently. */
  .wrap { position: absolute; inset: 0; pointer-events: none; }

  /* STOP pill — glass treatment matching the Tuscan-dusk chat-bar buttons.
     Floats just to the LEFT of the centered chat input bar (.chat-zone width is
     min(680px, 100% - 180px)); we offset from viewport center by half that width
     plus the pill's own width and a small gap. Show/hide animates opacity/blur
     only, so the positioning transform is never clobbered. */
  /* STOP pill — Tuscan-dusk treatment. Warm token-driven surface (no
     backdrop-filter blur: it must never re-blur the live 3D scene every frame),
     a terracotta accent dot housing the stop square, and full dark/light support
     via the --tts-* tokens pushed on the host by applyThemeMode(). Show/hide
     animates opacity/blur only, so the positioning transform is never clobbered. */
  .stop {
    /* RETIRED: the stop control now lives on the bottom-left mic button
       (mic-as-stop in ChatController). The pill used to float over the
       suggested-question chips and cover them. Keep the node so the module's
       wiring stays intact, but never render it. */
    display: none !important;
    position: absolute;
    bottom: 40px;
    left: 50%;
    transform: translateX(calc(-100% - 16px - (min(680px, 100vw - 180px) / 2)));
    display: inline-flex;
    align-items: center; gap: 9px;
    height: var(--stop-h);
    padding: 0 var(--stop-pad-x) 0 calc(var(--stop-pad-x) - 6px);
    border: 1px solid var(--tts-glass-border, rgba(244,239,230,0.14));
    border-radius: 999px;
    color: var(--tts-text, #f4efe6);
    font-family: "Manrope", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial;
    font-weight: 600;
    font-size: var(--stop-font);
    letter-spacing: .02em;
    cursor: pointer;
    background: var(--tts-stop-bg, rgba(34,26,18,0.92));
    box-shadow: 0 10px 28px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.06);
    transition: box-shadow .18s ease, opacity .18s ease, filter .18s ease, background .2s ease, border-color .2s ease;
    opacity: 0; filter: blur(2px);
    pointer-events: none;
  }
  .stop.visible { opacity: 1; filter: blur(0); pointer-events: auto; }
  .stop:hover {
    border-color: var(--tts-stop-accent, #d9603f);
    box-shadow: 0 12px 32px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.08);
  }
  .stop:active { filter: brightness(0.95); }

  /* terracotta dot (token-coloured) that holds the white stop square */
  .stop-dot {
    flex: 0 0 auto;
    display: inline-flex; align-items: center; justify-content: center;
    width: 22px; height: 22px; border-radius: 50%;
    background: var(--tts-stop-accent, #d9603f);
    box-shadow: 0 2px 6px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.18);
  }
  .stop-label { line-height: 1; }

  .stop-ico {
    width: 9px; height: 9px; display: inline-block;
    border-radius: 2px;
    background: #fff;
  }

  /* Compact volume pill — pinned to the bottom-left corner, glass treatment
     matching the Tuscan-dusk chat-bar buttons. */
  .vol {
    position: absolute;
    left: 22px;
    bottom: 22px;
    display: inline-flex;
    align-items: center;
    gap: var(--vol-gap);
    padding: var(--vol-pad-y) var(--vol-pad-x);
    border-radius: 999px;
    color: var(--tts-text, #f4efe6);
    background: var(--tts-glass-bg, rgba(28,34,44,0.65));
    backdrop-filter: blur(24px) saturate(140%);
    -webkit-backdrop-filter: blur(24px) saturate(140%);
    box-shadow: 0 8px 24px rgba(0,0,0,0.28), inset 0 1px 0 rgba(244,239,230,0.05);
    border: 1px solid var(--tts-glass-border, rgba(244,239,230,0.10));
    pointer-events: auto;
    transition: opacity .18s ease, filter .18s ease, background .2s ease, border-color .2s ease;
  }
  .vol:hover { border-color: var(--tts-glass-border-hot, rgba(244,239,230,0.18)); }

  .vol-btn {
    border: none; background: transparent; color: inherit;
    cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
    width: var(--vol-btn);
    height: var(--vol-btn);
    border-radius: 50%;
    opacity: .9;
    transition: background .15s ease, opacity .12s ease;
  }
  .vol-btn:hover { opacity: 1; background: var(--tts-hover-tint, rgba(244,239,230,0.10)); }
  .vol-btn.muted { opacity: .55; }

  /* Floating popover ABOVE the pill */
  .vol-box {
    position: absolute;
    bottom: calc(100% + 10px);
    left: 0;
    display: flex;
    align-items: center;
    gap: var(--popover-gap);
    opacity: 0;
    pointer-events: none;
    transform: translateY(6px);
    transition: opacity .18s ease, transform .18s ease, filter .18s ease;
    filter: blur(2px);
  }

  /* Invisible hitbox keeps hover active */
  .vol-box::before {
    content: "";
    position: absolute;
    top: -10px; left: -20px; right: -20px; bottom: -10px;
    background: rgba(0,0,0,0.001);
    pointer-events: auto;
    z-index: -1;
  }

  .vol:hover .vol-box,
  .vol.open .vol-box {
    opacity: 1; transform: translateY(0); pointer-events: auto; filter: blur(0);
  }

  /* Slider wrapper */
  .slider-wrap {
    position: relative;
    width: var(--slider-wrap-w);
    height: var(--slider-wrap-h);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Vertical slider (track is horizontal before rotation) */
  .vol-slider {
    -webkit-appearance: none; appearance: none;
    outline: none; border: 0;
    width: 150px; height: 36px;
    background: transparent;
    transform: rotate(270deg);
    transform-origin: center;
    display: block;
    touch-action: none;
  }

  /* Track */
  .vol-slider::-webkit-slider-runnable-track {
    height: var(--slider-track-h);
    background: linear-gradient(90deg, rgba(255,255,255,.7), rgba(255,255,255,.4));
    border-radius: 999px;
  }
  .vol-slider::-moz-range-track {
    height: var(--slider-track-h);
    background: linear-gradient(90deg, rgba(255,255,255,.7), rgba(255,255,255,.4));
    border-radius: 999px;
  }

  /* Thumb */
  .vol-slider::-webkit-slider-thumb {
    -webkit-appearance: none; appearance: none;
    width: var(--slider-knob); height: var(--slider-knob);
    border-radius: 50%;
    background: #fff;
    border: 1px solid rgba(0,0,0,.18);
    box-shadow: 0 2px 8px rgba(0,0,0,.25);
    cursor: pointer;
    margin-top: calc((var(--slider-knob) * -0.36)); /* centers knob on track after rotation */
  }
  .vol-slider:active::-webkit-slider-thumb { transform: scale(0.96); }

  .vol-slider::-moz-range-thumb {
    width: var(--slider-knob); height: var(--slider-knob);
    border-radius: 50%;
    background: #fff;
    border: 1px solid rgba(0,0,0,.18);
    box-shadow: 0 2px 8px rgba(0,0,0,.25);
    cursor: pointer;
  }

  .vol-pct {
    font-size: var(--pct-font);
    opacity: .9;
    min-width: 44px;
    text-align: right;
  }

  .ico { width: var(--icon); height: var(--icon); }

  /* ---------- Tablet ≤1280px ---------- */
  @media (max-width: 1280px) {
    :host {
      --stop-h: 48px;
      --stop-pad-x: 20px;
      --stop-font: 14.5px;

      --vol-pad-y: 9px;
      --vol-pad-x: 11px;
      --vol-btn: 30px;
      --icon: 17px;

      --slider-track-h: 5px;
      --slider-knob: 16px;
      --slider-wrap-w: 40px;
      --slider-wrap-h: 136px;

      --pct-font: 12px;
      --wrap-gap: 10px;
      --vol-gap: 9px;
      --popover-gap: 8px;
    }
  }

  /* ---------- Mobile ≤640px ---------- */
  @media (max-width: 640px) {
    :host {
      --stop-h: 44px;
      --stop-pad-x: 16px;
      --stop-font: 13.5px;

      --vol-pad-y: 8px;
      --vol-pad-x: 10px;
      --vol-btn: 28px;
      --icon: 16px;

      --slider-track-h: 4px;
      --slider-knob: 14px;
      --slider-wrap-w: 36px;
      --slider-wrap-h: 120px;

      --pct-font: 11.5px;
      --wrap-gap: 10px;
      --vol-gap: 8px;
      --popover-gap: 6px;
    }
  }

  /* ---------- Narrow screens ≤760px ----------
     The chat input bar shrinks and centers; parking the Stop pill to its left
     would push it off-screen. Center it just ABOVE the chat bar instead, with
     the volume staying in the bottom-left corner. */
  @media (max-width: 760px) {
    .stop {
      bottom: 104px;
      left: 50%;
      transform: translateX(-50%);
    }
    /* Phones have no in-app volume control — the device's hardware buttons own
       loudness, and the app runs at full volume (see ChatController TTS init). */
    .vol { display: none !important; }
  }
</style>

    <div class="wrap" part="wrap">
      <button class="stop" id="stopBtn" part="stop" aria-label="Stop playback">
        <span class="stop-dot" aria-hidden="true"><span class="stop-ico"></span></span>
        <span class="stop-label">Stop</span>
      </button>

      <div class="vol" id="volGroup" part="volume" aria-label="Volume">
        <button class="vol-btn" id="volBtn" aria-label="Open volume" title="Volume">
          <svg class="ico" id="iconVol" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M4 9v6h4l5 4V5L8 9H4z"></path>
            <path id="waves" d="M14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"></path>
          </svg>
        </button>

        <!-- floating popover -->
        <div class="vol-box" id="volBox" part="vol-box">
          <div class="slider-wrap">
            <input id="slider" class="vol-slider" type="range" min="0" max="1" step="0.01" value="1" aria-label="Volume slider">
          </div>
          <div class="vol-pct" id="pct">100%</div>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(host);

  host.style.setProperty("--accent", accent);
  host.style.setProperty("--accentSoft", accentSoft);

  // --- Light/Dark mode theming for Shadow DOM ---
  // The Tuscan-dusk shell drives mode via body[data-mode="light"] (and some flows
  // a legacy `light-mode` class). Push the glass tokens onto the host as custom
  // properties so the pills match the chat-bar buttons in either mode.
  const applyThemeMode = () => {
    const isLightMode =
      document.body.getAttribute('data-mode') === 'light' ||
      document.body.classList.contains('light-mode');
    if (isLightMode) {
      host.style.setProperty('--tts-glass-bg', 'rgba(255, 250, 240, 0.78)');
      host.style.setProperty('--tts-glass-border', 'rgba(31, 24, 16, 0.12)');
      host.style.setProperty('--tts-glass-border-hot', 'rgba(31, 24, 16, 0.24)');
      host.style.setProperty('--tts-text', '#2a221a');
      host.style.setProperty('--tts-hover-tint', 'rgba(31, 24, 16, 0.07)');
      host.style.setProperty('--tts-stop-accent', '#8b3a1e');
      host.style.setProperty('--tts-stop-bg', 'rgba(252, 247, 238, 0.94)');
    } else {
      host.style.setProperty('--tts-glass-bg', 'rgba(28, 34, 44, 0.65)');
      host.style.setProperty('--tts-glass-border', 'rgba(244, 239, 230, 0.10)');
      host.style.setProperty('--tts-glass-border-hot', 'rgba(244, 239, 230, 0.18)');
      host.style.setProperty('--tts-text', '#f4efe6');
      host.style.setProperty('--tts-hover-tint', 'rgba(244, 239, 230, 0.10)');
      host.style.setProperty('--tts-stop-accent', '#d9603f');
      host.style.setProperty('--tts-stop-bg', 'rgba(34, 26, 18, 0.92)');
    }
  };

  // Apply initial theme
  applyThemeMode();

  // Watch for mode changes on body (class OR data-mode attribute)
  const themeObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.attributeName === 'class' || mutation.attributeName === 'data-mode') {
        applyThemeMode();
        break;
      }
    }
  });
  themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-mode'] });

  // --- refs ---
  const $ = (id) => shadow.getElementById(id);
  const stopBtn = $("stopBtn");
  const volBtn = $("volBtn");
  const slider = $("slider");
  const pct = $("pct");
  const waves = shadow.getElementById("waves");
  const volGroup = $("volGroup");
  const volBox = $("volBox");

  // --- helpers ---
  const clamp01 = (x) => Math.max(0, Math.min(1, Number(x)));
  const fmtPct = (v) => `${Math.round(clamp01(v) * 100)}%`;
  const setPct = (v) => {
    pct.textContent = fmtPct(v);
  };
  const setMutedIcon = (muted) => {
    volBtn.classList.toggle("muted", !!muted);
    waves.setAttribute("opacity", muted ? "0.25" : "1");
  };
  const showStop = (_yes) => {
    /* RETIRED: stop control moved onto the mic button (mic-as-stop). The pill
       is never shown; keep the hook so event wiring stays intact. */
    stopBtn.classList.remove("visible");
  };

  // --- Drawer (popover) toggle ---
  let drawerOpen = false;
  const setDrawer = (open) => {
    drawerOpen = !!open;
    volGroup.classList.toggle("open", drawerOpen);
  };

  // Toggle on click (mobile + desktop)
  const onVolBtnClick = (e) => {
    e.stopPropagation();
    setDrawer(!drawerOpen);
  };

  // Close when clicking outside — use composedPath so shadow clicks don't count as “outside”
  const onDocClick = (e) => {
    const path = typeof e.composedPath === "function" ? e.composedPath() : [];
    if (!path.includes(host)) setDrawer(false);
  };

  // Keep hover popover interactive (don’t close on internal clicks)
  const onShadowClick = (e) => e.stopPropagation();

  volBtn.addEventListener("click", onVolBtnClick);
  document.addEventListener("click", onDocClick);
  shadow.addEventListener("click", onShadowClick);

  // --- initial UI sync ---
  try {
    slider.value = String(tts.getVolume());
  } catch {
    slider.value = "0.7";
  }
  setPct(slider.value);
  setMutedIcon(!!tts.muted);

  // --- Stop click ---
  const onStopClick = async () => {
    stopBtn.disabled = true;
    try {
      await (tts.stopNow ? tts.stopNow({ fadeMs }) : tts.cancel?.());
    } catch (_) {
    } finally {
      stopBtn.disabled = false;
      showStop(false);
    }
  };
  stopBtn.addEventListener("click", onStopClick);

  // --- volume ---
  const onSliderInput = (e) => {
    const v = clamp01(e.target.value);
    try {
      tts.setVolume?.(v);
    } catch (_) {}
    if (tts.muted && v > 0 && typeof tts.unmute === "function") tts.unmute();
    setPct(v);
  };
  slider.addEventListener("input", onSliderInput);

  // NOTE: removed the double-click mute/zero behavior on the volume icon.

  // --- wire engine → UI ---
  const hasEvents =
    typeof tts.on === "function" && typeof tts.off === "function";
  let pollTimer = null;

  const onStopVis = (ev) => {
    const visible = !!(ev && ev.detail && ev.detail.visible);
    showStop(visible);
  };
  const onStart = () => {};
  const onEnd = () => showStop(false);
  const onVolEvent = (ev) => {
    const vol =
      ev && ev.detail && typeof ev.detail.volume === "number"
        ? ev.detail.volume
        : (tts.getVolume?.() ?? Number(slider.value));
    slider.value = String(clamp01(vol));
    setPct(vol);
  };
  const onMuteEvent = () => setMutedIcon(!!tts.muted);

  if (hasEvents) {
    tts.on("stop-visibility", onStopVis);
    tts.on("audio-start", onStart);
    tts.on("audio-end", onEnd);
    tts.on("volume-change", onVolEvent);
    tts.on("mute-change", onMuteEvent);
  } else {
    // Polling fallback
    let lastActive = null;
    pollTimer = setInterval(() => {
      const active = !!(
        tts.playing ||
        (tts._activeNodes && tts._activeNodes.size > 0)
      );
      if (active !== lastActive) {
        showStop(active);
        lastActive = active;
      }
      try {
        const v = tts.getVolume?.();
        if (typeof v === "number" && v != Number(slider.value)) {
          slider.value = String(clamp01(v));
          setPct(v);
          setMutedIcon(!!tts.muted);
        }
      } catch (_) {}
    }, 150);
  }

  // public controller
  const api = {
    destroy() {
      try {
        stopBtn.removeEventListener("click", onStopClick);
        slider.removeEventListener("input", onSliderInput);
        volBtn.removeEventListener("click", onVolBtnClick);
        shadow.removeEventListener("click", onShadowClick);
        document.removeEventListener("click", onDocClick);

        if (hasEvents) {
          tts.off("stop-visibility", onStopVis);
          tts.off("audio-start", onStart);
          tts.off("audio-end", onEnd);
          tts.off("volume-change", onVolEvent);
          tts.off("mute-change", onMuteEvent);
        }
        
        // Clean up theme observer
        themeObserver.disconnect();
      } catch (_) {}

      if (pollTimer) clearInterval(pollTimer);
      if (host.isConnected) host.remove();
      window.__ttsOverlayMounted = null;
    },
    setPosition(pos) {
      Object.assign(host.style, { top: "", right: "", bottom: "", left: "" });
      const PAD = opts.padding === undefined ? 18 : Number(opts.padding);
      const map = {
        "bottom-right": { bottom: `${PAD}px`, right: `${PAD}px` },
        "bottom-left": { bottom: `${PAD}px`, left: `${PAD}px` },
        "top-right": { top: `${PAD}px`, right: `${PAD}px` },
        "top-left": { top: `${PAD}px`, left: `${PAD}px` },
      };
      Object.assign(host.style, map[pos] || map["bottom-right"]);
    },
    root: host,
    shadow,
  };

  window.__ttsOverlayMounted = api;
  return api;
}
